import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, setAccessToken, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=logged out, object=logged in
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production" && err.response?.status && err.response.status !== 401) {
        console.warn("Auth check failed:", err.message);
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setUser(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setAccessToken(data.access_token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      return true;
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("Logout request failed:", err.message);
      }
    }
    setAccessToken(null);
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, error, login, logout, refresh }),
    [user, error, login, logout, refresh]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
